/**
 * Asset Domain Schemas
 *
 * Unified asset validation schemas with inferred TypeScript types.
 * Single source of truth for asset domain - aligned with backend asset.model.ts
 *
 * Asset types are now dynamic (factory-configurable). The schema uses z.string()
 * instead of z.enum() to accept any factory-defined type. See asset-type-config.schemas.ts
 * for the type configuration system and defaults.
 */

import { z } from 'zod';

// Asset type schema — accepts any string (factory-configurable types)
export const assetTypeSchema = z.string().min(1, 'assets.validation.type_required');

// Asset status enum (matches backend asset status field)
export const assetStatusSchema = z.enum(['active', 'inactive', 'maintenance', 'retired']);

// Core asset domain schema (matches backend Asset model)
export const assetSchema = z.object({
  id: z.string(),
  name: z.string().min(1, 'assets.validation.name_required').max(200),
  type: assetTypeSchema,
  tenantId: z.string(),
  factoryId: z.string(),
  tagId: z.string().optional(),
  description: z.string().max(500).optional(),
  serialNumber: z.string().optional(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  imageUrl: z.string().optional(),
  status: assetStatusSchema.default('active'),
  metadata: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().default(true),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

// Asset creation form schema (aligned with backend create validation)
export const assetCreateFormSchema = z.object({
  factoryId: z.string().min(1, 'validation.factory_id_required'),
  name: z.string().min(1, 'assets.validation.name_required').max(200),
  type: assetTypeSchema,
  tagId: z.string().optional(),
  description: z.string().max(500).optional(),
  serialNumber: z.string().optional(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  imageUrl: z.string().url('assets.validation.invalid_image_url').optional(),
  status: assetStatusSchema.default('active'),
  metadata: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().default(true),
});

// Asset update form schema (aligned with backend update validation - no type/tagId)
export const assetUpdateFormSchema = z.object({
  name: z.string().min(1, 'assets.validation.name_required').max(200).optional(),
  description: z.string().max(500).optional(),
  serialNumber: z.string().optional(),
  manufacturer: z.string().optional(),
  model: z.string().optional(),
  imageUrl: z.string().url('assets.validation.invalid_image_url').optional(),
  status: assetStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  isActive: z.boolean().optional(),
});

// Asset filter schema for search/filtering
export const assetFilterSchema = z.object({
  factoryId: z.string().min(1, 'validation.factory_id_required'),
  search: z.string().optional(),
  type: z.string().optional(),
  status: assetStatusSchema.optional(),
  isActive: z.boolean().optional(),
  page: z.coerce.number().min(1).default(1),
  limit: z.coerce.number().min(1).max(100).default(20),
});

// Asset list response schema
export const assetListResponseSchema = z.object({
  assets: z.array(assetSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
  hasMore: z.boolean(),
});

// Inferred TypeScript types - single source of truth
export type AssetType = z.infer<typeof assetTypeSchema>;
export type AssetStatus = z.infer<typeof assetStatusSchema>;
export type Asset = z.infer<typeof assetSchema>;
export type AssetCreateForm = z.infer<typeof assetCreateFormSchema>;
export type AssetUpdateForm = z.infer<typeof assetUpdateFormSchema>;
export type AssetFilter = z.infer<typeof assetFilterSchema>;
export type AssetListResponse = z.infer<typeof assetListResponseSchema>;

export const assetStatusLabels: Record<AssetStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

// Validation helpers
export const validateAssetStatus = (status: unknown): status is AssetStatus => {
  return assetStatusSchema.safeParse(status).success;
};

export const validateAsset = (asset: unknown): asset is Asset => {
  return assetSchema.safeParse(asset).success;
};
